import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Lock, Loader2 } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPOSIT_PACKAGES = [
  { id: "deposit_small", amount: 50, currency: "€", label_key: "small" },
  { id: "deposit_medium", amount: 150, currency: "€", label_key: "medium" },
  { id: "deposit_large", amount: 500, currency: "€", label_key: "large" },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export const QuickBooking = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    destination: "",
    departure_date: "",
    return_date: "",
    travelers: 2,
    service_type: "package",
    name: "",
    email: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [quoteId, setQuoteId] = useState(null);
  const [selectedPkg, setSelectedPkg] = useState("deposit_small");
  const [payLoading, setPayLoading] = useState(false);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === "travelers" ? Number(value) : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.destination) {
      toast.error("Destination required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, form);
      toast.success(t.booking.success);
      setQuoteId(data.id);
    } catch (err) {
      toast.error(t.hero.error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeposit = async () => {
    setPayLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        package_id: selectedPkg,
        quote_id: quoteId,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("no url");
      }
    } catch (err) {
      toast.error(t.deposit.error);
      setPayLoading(false);
    }
  };

  const resetFlow = () => {
    setQuoteId(null);
    setForm({
      destination: "",
      departure_date: "",
      return_date: "",
      travelers: 2,
      service_type: "package",
      name: "",
      email: "",
      phone: "",
    });
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative bg-[#EAE3D2] text-[#0B1416] py-28 md:py-40 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start"
        >
          <div className="lg:col-span-5">
            <div className="overline text-[#0D7A7D]">— {t.booking.eyebrow}</div>
            <h2
              data-testid="booking-title"
              className="font-heading-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95] mt-6"
            >
              {t.booking.title}
            </h2>
            <p className="mt-6 text-lg font-light opacity-75 max-w-md">
              {t.booking.subtitle}
            </p>

            {/* Decorative big number */}
            <div
              aria-hidden
              className="font-italiana text-[#0B1416]/8 mt-16 leading-none select-none pointer-events-none"
              style={{ fontSize: "clamp(120px, 22vw, 320px)" }}
            >
              60″
            </div>
          </div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {!quoteId ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="booking-form"
                  onSubmit={submit}
                  className="bg-[#0B1416] text-[#F5F1E8] p-8 md:p-12 relative"
                >
                  <div className="absolute -top-3 left-8 bg-[#1BB6B9] text-[#0B1416] px-3 py-1 text-[10px] tracking-[0.28em] uppercase font-medium">
                    Express
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="overline opacity-60 block mb-2">
                        {t.booking.destination}
                      </label>
                      <input
                        data-testid="booking-destination"
                        className="tt-input"
                        name="destination"
                        placeholder="Marrakech, Dubai, Istanbul…"
                        value={form.destination}
                        onChange={change}
                        required
                      />
                    </div>

                    <div>
                      <label className="overline opacity-60 block mb-2">
                        {t.booking.departure}
                      </label>
                      <input
                        data-testid="booking-departure"
                        className="tt-input"
                        type="date"
                        name="departure_date"
                        value={form.departure_date}
                        onChange={change}
                      />
                    </div>
                    <div>
                      <label className="overline opacity-60 block mb-2">
                        {t.booking.return}
                      </label>
                      <input
                        data-testid="booking-return"
                        className="tt-input"
                        type="date"
                        name="return_date"
                        value={form.return_date}
                        onChange={change}
                      />
                    </div>

                    <div>
                      <label className="overline opacity-60 block mb-2">
                        {t.booking.travelers}
                      </label>
                      <input
                        data-testid="booking-travelers"
                        className="tt-input"
                        type="number"
                        min={1}
                        max={40}
                        name="travelers"
                        value={form.travelers}
                        onChange={change}
                      />
                    </div>
                    <div>
                      <label className="overline opacity-60 block mb-2">
                        {t.booking.service}
                      </label>
                      <select
                        data-testid="booking-service"
                        className="tt-input appearance-none cursor-pointer"
                        name="service_type"
                        value={form.service_type}
                        onChange={change}
                        style={{ backgroundImage: "none" }}
                      >
                        <option value="flight" className="bg-[#0B1416]">
                          {t.booking.services_opts.flight}
                        </option>
                        <option value="hotel" className="bg-[#0B1416]">
                          {t.booking.services_opts.hotel}
                        </option>
                        <option value="transport" className="bg-[#0B1416]">
                          {t.booking.services_opts.transport}
                        </option>
                        <option value="package" className="bg-[#0B1416]">
                          {t.booking.services_opts.package}
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="overline opacity-60 block mb-2">
                        {t.booking.name}
                      </label>
                      <input
                        data-testid="booking-name"
                        className="tt-input"
                        name="name"
                        value={form.name}
                        onChange={change}
                      />
                    </div>
                    <div>
                      <label className="overline opacity-60 block mb-2">
                        {t.booking.email}
                      </label>
                      <input
                        data-testid="booking-email"
                        className="tt-input"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={change}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="overline opacity-60 block mb-2">
                        {t.booking.phone}
                      </label>
                      <input
                        data-testid="booking-phone"
                        className="tt-input"
                        name="phone"
                        value={form.phone}
                        onChange={change}
                      />
                    </div>
                  </div>

                  <button
                    data-testid="booking-submit"
                    type="submit"
                    disabled={submitting}
                    className="tt-btn tt-btn-primary mt-10 w-full justify-center disabled:opacity-60"
                  >
                    {submitting ? t.booking.submitting : t.booking.submit}
                    <ArrowUpRight size={14} />
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="deposit"
                  data-testid="deposit-step"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="bg-[#0B1416] text-[#F5F1E8] p-8 md:p-12 relative"
                >
                  <div className="absolute -top-3 left-8 bg-[#6EE3E6] text-[#0B1416] px-3 py-1 text-[10px] tracking-[0.28em] uppercase font-medium">
                    {t.deposit.badge}
                  </div>

                  <div className="flex items-start gap-4 mb-8">
                    <ShieldCheck size={28} className="text-[#6EE3E6] shrink-0 mt-1" strokeWidth={1.4} />
                    <div>
                      <div className="font-heading text-3xl md:text-4xl leading-tight">
                        {t.deposit.title}
                      </div>
                      <div className="text-sm opacity-70 mt-2 leading-relaxed">
                        {t.deposit.subtitle}
                      </div>
                    </div>
                  </div>

                  {/* Package selector */}
                  <div className="grid grid-cols-3 gap-3 mb-8">
                    {DEPOSIT_PACKAGES.map((pkg) => {
                      const selected = selectedPkg === pkg.id;
                      return (
                        <button
                          type="button"
                          key={pkg.id}
                          data-testid={`deposit-pkg-${pkg.id}`}
                          onClick={() => setSelectedPkg(pkg.id)}
                          className={`relative p-4 md:p-5 text-left border transition-all ${
                            selected
                              ? "border-[#6EE3E6] bg-[#6EE3E6]/8"
                              : "border-[#F5F1E8]/15 hover:border-[#F5F1E8]/35"
                          }`}
                        >
                          <div className="font-italiana text-3xl md:text-4xl leading-none">
                            {pkg.currency}
                            {pkg.amount}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.24em] mt-3 opacity-70">
                            {t.deposit.pkg_labels[pkg.label_key]}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Guarantees */}
                  <ul className="space-y-2 mb-8 text-[13px] opacity-75">
                    {t.deposit.guarantees.map((g, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="text-[#6EE3E6]" style={{ fontSize: 8 }}>●</span>
                        {g}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid="deposit-pay"
                    type="button"
                    onClick={handleDeposit}
                    disabled={payLoading}
                    className="tt-btn tt-btn-primary w-full justify-center disabled:opacity-60"
                  >
                    {payLoading ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        {t.deposit.loading}
                      </>
                    ) : (
                      <>
                        <Lock size={12} />
                        {t.deposit.cta}
                        <ArrowUpRight size={14} />
                      </>
                    )}
                  </button>

                  <button
                    data-testid="deposit-skip"
                    type="button"
                    onClick={resetFlow}
                    className="mt-4 w-full text-[11px] uppercase tracking-[0.28em] opacity-50 hover:opacity-90 transition-opacity py-2"
                  >
                    {t.deposit.skip}
                  </button>

                  <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.28em] opacity-40">
                    <Lock size={10} />
                    Stripe · Test mode · SSL secured
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
